/**
 * Wholesale Reports — read-only history of past committed runs.
 * Top half: most-recent-first table of WholesaleRunSummary rows. Click a
 * row to load the run detail; the bottom half then shows the run's
 * header info plus the per-vehicle match table reused from the Adesa
 * Cleared List page.
 */
import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"

import AuctionStatusPill from "../../../components/wholesale-auction/AuctionStatusPill"
import MatchResultsTable from "../../../components/wholesale/MatchResultsTable"
import Layout from "../../../components/Layout"
import {
  WholesaleRunSummary,
  WholesaleTab,
  WholesaleVehicleGroups,
  groupVehicles,
} from "../../../api/wholesale"
import {
  AuctionRunSummary,
  listAuctionRuns,
} from "../../../api/wholesaleAuction"
import { THEME } from "../../../config/theme"
import { WholesaleProvider, useWholesale } from "../../../context/WholesaleContext"
import { useDealerGroup } from "../../../context/DealerGroupContext"
import DealerGroupRequiredNotice from "../../../components/DealerGroupRequiredNotice"

export default function WholesaleReportsPage() {
  return (
    <Layout>
      <WholesaleProvider>
        <WholesaleReportsPageContent />
      </WholesaleProvider>
    </Layout>
  )
}

function WholesaleReportsPageContent() {
  const {
    runs,
    runsLoading,
    runsError,
    refreshRuns,
    runDetail,
    runDetailLoading,
    runDetailError,
    loadRunDetail,
  } = useWholesale()
  const { dealerGroupId } = useDealerGroup()

  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<WholesaleTab>("cleared")

  useEffect(() => {
    refreshRuns()
  }, [refreshRuns])

  // Auto-select the most recent run when the list arrives.
  useEffect(() => {
    if (selectedRunId === null && runs.length > 0) {
      setSelectedRunId(runs[0].id)
    }
  }, [runs, selectedRunId])

  // Fetch detail when the selection changes.
  useEffect(() => {
    if (selectedRunId !== null) {
      loadRunDetail(selectedRunId)
    }
  }, [selectedRunId, loadRunDetail])

  const detailGroups = useMemo<WholesaleVehicleGroups | null>(
    () => groupVehicles(runDetail),
    [runDetail],
  )

  if (dealerGroupId === null) {
    return (
      <div style={{ padding: THEME.spacing.lg, fontFamily: THEME.fonts.primary }}>
        <DealerGroupRequiredNotice surface="wholesale reports" />
      </div>
    )
  }

  return (
    <div
      style={{
        padding: THEME.spacing.lg,
        fontFamily: THEME.fonts.primary,
        display: "flex",
        flexDirection: "column",
        gap: THEME.spacing.lg,
      }}
    >
      <h1
        style={{
          margin: 0,
          fontSize: 24,
          fontWeight: 600,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        Wholesale Reports
      </h1>

      <AuctionRunsSection />

      <SectionHeading>Adesa Cleared List Runs</SectionHeading>
      <RunsTable
        runs={runs}
        loading={runsLoading}
        error={runsError}
        selectedRunId={selectedRunId}
        onSelect={setSelectedRunId}
      />

      {selectedRunId !== null && (
        <>
          {runDetailError && !runDetailLoading && (
            <span style={{ color: THEME.colors.semantic.danger }}>{runDetailError}</span>
          )}
          {runDetail && !runDetailLoading && (
            <RunDetailHeader run={runDetail.run} />
          )}
          <MatchResultsTable
            groups={detailGroups}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            loading={runDetailLoading}
          />
        </>
      )}
    </div>
  )
}

interface RunsTableProps {
  runs: WholesaleRunSummary[]
  loading: boolean
  error: string | null
  selectedRunId: number | null
  onSelect: (id: number) => void
}

function RunsTable({ runs, loading, error, selectedRunId, onSelect }: RunsTableProps) {
  if (loading) {
    return (
      <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
        Loading runs…
      </span>
    )
  }
  if (error) {
    return <span style={{ color: THEME.colors.semantic.danger }}>{error}</span>
  }
  if (runs.length === 0) {
    return (
      <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
        No runs yet — commit your first run from the Adesa Cleared List page.
      </span>
    )
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.sm,
      }}
    >
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontSize: 13,
          fontFamily: THEME.fonts.primary,
        }}
      >
        <thead>
          <tr style={{ background: THEME.colors.navy }}>
            {["Run", "Started", "By", "Cleared", "Not Cleared", "Unmatched", "Adesa File", "Tekion File"].map((h) => (
              <th
                key={h}
                style={{
                  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  color: THEME.colors.white,
                  textAlign: "left",
                  borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const selected = run.id === selectedRunId
            return (
              <tr
                key={run.id}
                onClick={() => onSelect(run.id)}
                style={{
                  background: selected
                    ? "rgba(13, 26, 66, 0.06)"
                    : THEME.colors.neutral.light.surface,
                  cursor: "pointer",
                }}
              >
                <Cell selected={selected}>#{run.id}</Cell>
                <Cell selected={selected}>{formatDateTime(run.started_at)}</Cell>
                <Cell selected={selected}>{run.initiated_by_username ?? "—"}</Cell>
                <Cell selected={selected}>{run.cleared_count}</Cell>
                <Cell selected={selected}>{run.not_cleared_count}</Cell>
                <Cell selected={selected}>{run.unmatched_adesa_count}</Cell>
                <Cell selected={selected}>{run.adesa_filename ?? "—"}</Cell>
                <Cell selected={selected}>{run.tekion_filename ?? "—"}</Cell>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Cell({ children, selected }: { children: React.ReactNode; selected: boolean }) {
  return (
    <td
      style={{
        padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
        color: THEME.colors.neutral.light.textPrimary,
        borderTop: `1px solid ${THEME.colors.neutral.light.border}`,
        fontWeight: selected ? 500 : 400,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </td>
  )
}

function RunDetailHeader({ run }: { run: WholesaleRunSummary }) {
  return (
    <div
      style={{
        padding: THEME.spacing.md,
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.md,
        background: THEME.colors.neutral.light.surface,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: THEME.spacing.md,
        fontSize: 13,
      }}
    >
      <KV label="Run" value={`#${run.id}`} />
      <KV label="Started" value={formatDateTime(run.started_at)} />
      <KV label="Status" value={run.status} />
      <KV label="By" value={run.initiated_by_username ?? "—"} />
      <KV
        label="Adesa"
        value={
          run.adesa_filename
            ? `${run.adesa_filename} (${run.adesa_row_count?.toLocaleString() ?? "?"} rows)`
            : "—"
        }
      />
      <KV
        label="Tekion"
        value={
          run.tekion_filename
            ? `${run.tekion_filename} (${run.tekion_row_count?.toLocaleString() ?? "?"} rows)`
            : "—"
        }
      />
      <KV
        label="Applied Families"
        value={run.applied_families.length > 0 ? run.applied_families.join(", ") : "—"}
      />
    </div>
  )
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: THEME.colors.neutral.light.textSecondary,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ color: THEME.colors.neutral.light.textPrimary }}>{value}</div>
    </div>
  )
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

// ---------------------------------------------------------------------------
// Adesa Run List section — listed alongside Cleared List runs on the
// Reports page. Calls the auction API directly (rather than mounting
// WholesaleAuctionProvider) since the section only needs the list call.
// ---------------------------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 16,
        fontWeight: 600,
        color: THEME.colors.neutral.light.textPrimary,
        marginTop: THEME.spacing.md,
      }}
    >
      {children}
    </div>
  )
}

function AuctionRunsSection() {
  const [runs, setRuns] = useState<AuctionRunSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listAuctionRuns()
      .then((res) => {
        if (cancelled) return
        setRuns(res.runs)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : "Failed to load auction runs")
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <>
      <SectionHeading>Adesa Run List Runs</SectionHeading>
      <AuctionRunsTable runs={runs} loading={loading} error={error} />
    </>
  )
}

interface AuctionRunsTableProps {
  runs: AuctionRunSummary[]
  loading: boolean
  error: string | null
}

function AuctionRunsTable({ runs, loading, error }: AuctionRunsTableProps) {
  if (loading) {
    return (
      <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
        Loading auction runs…
      </span>
    )
  }
  if (error) {
    return <span style={{ color: THEME.colors.semantic.danger }}>{error}</span>
  }
  if (runs.length === 0) {
    return (
      <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
        No auction runs yet — build your first from the Adesa Run List page.
      </span>
    )
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.sm,
      }}
    >
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontSize: 13,
          fontFamily: THEME.fonts.primary,
        }}
      >
        <thead>
          <tr style={{ background: THEME.colors.navy }}>
            {["Run", "Auction Date", "Status", "Author", "Cars", "Adesa File", "VAUTO File", ""].map((h) => (
              <th
                key={h}
                style={{
                  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  color: THEME.colors.white,
                  textAlign: "left",
                  borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr
              key={run.id}
              style={{
                background: THEME.colors.neutral.light.surface,
              }}
            >
              <Cell selected={false}>#{run.id}</Cell>
              <Cell selected={false}>{formatDate(run.auction_date)}</Cell>
              <Cell selected={false}><AuctionStatusPill status={run.status} /></Cell>
              <Cell selected={false}>{run.initiated_by_name ?? "—"}</Cell>
              <Cell selected={false}>{run.vehicle_count}</Cell>
              <Cell selected={false}>{run.adesa_filename ?? "—"}</Cell>
              <Cell selected={false}>{run.vauto_filename ?? "—"}</Cell>
              <Cell selected={false}>
                <Link
                  to={`/inventory/wholesale/adesa-run-list?runId=${run.id}`}
                  style={{
                    color: THEME.colors.navy,
                    fontWeight: 500,
                    textDecoration: "none",
                  }}
                >
                  Open →
                </Link>
              </Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}


function formatDate(iso: string | null): string {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}
